import { isStorageAvailable as storageAvailable } from 'js/utils/storage';

/**
 * The default amount of time, in milliseconds, that messages are dismissed for.
 *
 * @type {number}
 */
export const DEFAULT_DISMISSAL_DURATION = (
  window.nexstar.alert_banners.message_dismissal_duration * 1000
);

/**
 * The default storage layer for dismissed messages.
 *
 * @type {string}
 */
// eslint-disable-next-line max-len
export const DEFAULT_DISMISSAL_STORAGE = window.nexstar.alert_banners.message_dismissal_storage;

/**
 * Whether the client currently supports dismissed messages.
 *
 * @return {boolean}
 */
export function clientSupportsDismissal() {
  return storageAvailable('localStorage') && storageAvailable('sessionStorage');
}

/**
 * Store a UUID in the set of messages dismissed for the current client.
 *
 * @param {string} uuid Message UUID.
 * @param {Date} dismissedAt Date when the message was dismissed.
 * @param {string} storage Storage layer to use: localStorage or sessionStorage.
 */
export function storeDismissedMessage(uuid, dismissedAt, dismissFor, storage) {
  if (! clientSupportsDismissal()) {
    return;
  }

  const current = retrieveDismissedMessages(new Date(), [storage]);
  const updated = {
    ...current,
    [uuid]: {
      uuid,
      dismissedAt: dismissedAt.toISOString(),
      dismissFor,
    },
  };

  try {
    window[storage].setItem(
      'ns.alertBanners.dismissedMessages',
      JSON.stringify(updated),
    );
  } catch (err) {
    // Ignore.
  }
}

/**
 * Retrieve the client's dismissed messages.
 *
 * @param {Date} relativeTo Retrieve messages that were dismissed within the dismissal duration relative to this date.
 * @param {String[]} stores Storage layer(s) to retreive from, defauls to local and session.
 * @return {{}} Dismissed message UUIDs and their dismissal ISO times keyed by UUID.
 */
export function retrieveDismissedMessages(
  relativeTo,
  stores = ['localStorage', 'sessionStorage'],
) {
  if (! clientSupportsDismissal()) {
    return {};
  }

  try {
    const storageKey = 'ns.alertBanners.dismissedMessages';
    const stored = stores.reduce((acc, store) => {
      const raw = window[store].getItem(storageKey);
      return { ...acc, ...JSON.parse(raw) };
    }, {});

    return Object.keys(stored).reduce(
      (previous, key) => {
        const newSet = previous;

        const message = stored[key];
        const dismissedAt = new Date(message.dismissedAt).getTime();

        // Include in the set if the current time hasn't yet passed the time of dismissal plus the dismissal duration.
        if (relativeTo.getTime() < dismissedAt + message.dismissFor) {
          newSet[message.uuid] = message;
        }

        return newSet;
      },
      {},
    );
  } catch (err) {
    return {};
  }
}
